/**
 * 画面をまたいで見た目を揃えるための共通クラス。
 * 基準はライフプラン・収支ページのコンパクトな寸法（見出し text-lg / ボタン px-3 py-1.5）。
 * 新しいボタンを足すときはここから選び、どれにも当てはまらないときだけ個別に書く。
 */

/** ページ見出し（左上のタイトル）とその下の補足文 */
export const pageTitle = 'text-lg font-semibold text-gray-900 dark:text-gray-100'
export const pageSubtitle = 'text-xs text-gray-500 dark:text-gray-400'

/** ページ内のセクション見出し。ページタイトルより 1 段小さくする */
export const sectionTitle = 'text-base font-semibold text-gray-900 dark:text-gray-100'
/** モーダルのタイトル */
export const modalTitle = 'text-lg font-semibold text-gray-900 dark:text-gray-100'
/** セクション内の小見出し・フォームのグループ名 */
export const subheading = 'text-sm font-semibold text-gray-700 dark:text-gray-300'

const btnBase =
  'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
/** 既定サイズ。ページのアクションとモーダルのフッターはこれ */
const sizeMd = 'px-3 py-1.5 text-sm'
/** 表の行内・ツールバーなど、行の高さを増やしたくない場所 */
const sizeSm = 'px-2 py-1 text-xs'

/** 主要アクション（保存・追加など、画面に 1 つ） */
export const btnPrimary = `${btnBase} ${sizeMd} bg-indigo-600 text-white hover:bg-indigo-700`
export const btnPrimarySm = `${btnBase} ${sizeSm} bg-indigo-600 text-white hover:bg-indigo-700`
/** 幅いっぱいの主要アクション（ログイン・登録） */
export const btnPrimaryBlock = `${btnBase} w-full px-4 py-2 text-sm bg-indigo-600 text-white hover:bg-indigo-700`

/** 副アクション（キャンセル・前月/翌月など） */
export const btnSecondary = `${btnBase} ${sizeMd} border border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800`
export const btnSecondarySm = `${btnBase} ${sizeSm} border border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800`

/** 破壊的アクション（削除） */
export const btnDanger = `${btnBase} ${sizeMd} border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950`
export const btnDangerSm = `${btnBase} ${sizeSm} border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950`

/** 補助的だが目立たせたいアクション（今月へジャンプ・再取得など） */
export const btnAccent = `${btnBase} ${sizeMd} border border-indigo-200 text-indigo-600 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-400 dark:hover:bg-indigo-950`
export const btnAccentSm = `${btnBase} ${sizeSm} border border-indigo-200 text-indigo-600 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-400 dark:hover:bg-indigo-950`

/** 前月/翌月などの矢印。記号が小さくならないよう text-sm を保つ */
export const btnNav = `${btnBase} px-2 py-1 text-sm border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100`

/** 枠なし。モーダルの「閉じる」など、押されなくてよいもの */
export const btnGhost = `${btnBase} ${sizeMd} text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100`

/** アイコンだけのボタン（余白を持たせず、色だけで状態を出す） */
const btnIconBase = 'shrink-0 transition-colors text-gray-400 dark:text-gray-500'
export const btnIcon = `${btnIconBase} hover:text-gray-600 dark:hover:text-gray-300`
export const btnIconAccent = `${btnIconBase} hover:text-indigo-600 dark:hover:text-indigo-400`
export const btnIconDanger = `${btnIconBase} hover:text-red-600 dark:hover:text-red-400`

/** テキストリンク風のボタン */
export const btnLink =
  'text-xs font-medium text-indigo-600 transition-colors hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300'
export const btnLinkMuted =
  'text-xs text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
export const btnLinkDanger =
  'text-xs text-gray-500 transition-colors hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400'

/** 表示切替のセグメンテッドコントロール。group で囲んで item を並べる */
export const segGroup = 'inline-flex rounded-lg border border-gray-200 p-0.5 dark:border-gray-700'
export const segItem = (active: boolean) =>
  `px-3 py-1 text-sm rounded-md transition-colors ${
    active
      ? 'bg-indigo-600 text-white'
      : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100'
  }`

/** 収支タイプ（支出/収入/振替）の選択トグル。取引と定期で同じ見た目にする */
export const typeToggleGroup = 'flex rounded-lg border border-gray-200 overflow-hidden dark:border-gray-700'
const typeToggleActive: Record<'expense' | 'income' | 'transfer', string> = {
  expense: 'bg-red-600 text-white',
  income: 'bg-green-600 text-white',
  transfer: 'bg-gray-600 text-white',
}
export const typeToggleItem = (type: 'expense' | 'income' | 'transfer', active: boolean) =>
  `flex-1 py-2 text-sm font-medium transition-colors ${
    active
      ? typeToggleActive[type]
      : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
  }`

/** 口座・クレジットカードのカードを並べるグリッド。画面幅で 1〜4 列 */
export const cardGrid = 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
